<script setup lang="ts">
import { computed, ref } from "vue";

import BaseTextarea from "./BaseTextarea.vue";

defineOptions({ inheritAttrs: false });

const model = defineModel<string>({ required: true });

const props = defineProps<{ maxlength: number; bordered?: boolean; readonly?: boolean }>();

const isFocused = ref(false);
const isValid = ref(true);

const textLength = computed(() => model.value.length);
const isLengthExceeded = computed(() => textLength.value > props.maxlength);

function validate() {
  isValid.value = !isLengthExceeded.value;
}

function handleFocus() {
  isFocused.value = true;
  validate();
}

function handleBlur() {
  isFocused.value = false;
}
</script>

<template>
  <div class="max-length-textarea">
    <BaseTextarea
      v-bind="$attrs"
      ref="baseTextarea"
      v-model="model"
      class="base-textarea"
      :class="{ error: !isValid, 'no-border': !bordered }"
      :maxlength
      :readonly
      @update:model-value="validate"
      @blur="handleBlur"
      @focus="handleFocus"
    />
    <span v-if="isFocused && !readonly" class="message">
      <p
        class="length"
        :class="{ error: isLengthExceeded }"
        :aria-label="$t('textarea.lengthIndicator.aria')"
      >
        {{ $t("textarea.lengthIndicator", { textLength, maxLength: maxlength }) }}
      </p>
    </span>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/font";
@use "@/styles/variables/colors";

.max-length-textarea {
  width: 100%;
  display: flex;
  position: relative;

  .message {
    position: absolute;
    right: 5px;
    bottom: 2px;
    width: 60px;
    text-align: center;
    padding: 0.5rem 0;

    p {
      margin: 0.125rem 0 0;
      font-size: font.$size-small;
      vertical-align: middle;

      &.error {
        color: colors.$destructive;
      }
    }
  }

  .base-textarea {
    padding-right: 65px;

    &.no-border {
      outline: none;
      border: none;
    }

    &::placeholder {
      color: colors.$text-placeholder;
    }
  }
}
</style>
